/**
 * lib/chartFigure.ts — port of app/callbacks/chart.py build_figure().
 *
 * Returns a Plotly figure config (data + layout) given the bar series
 * and the selected overlays/subpanels. Plotly.js consumes the same
 * JSON shape Python emits, so this is a near-literal port — same
 * trace kinds (candlestick, scatter, bar, histogram), same xaxis/yaxis
 * subplot structure, same dark theme overrides.
 */
import type { Bar, OverlayId, SubpanelId } from '@/types';
import {
  SMA, BB, RSI, MACD, ATR, OBV, STOCH, ADON, VPRO, PIVOT, VWAP,
} from '@/lib/indicators';
import { readThemeColors } from '@/store';

// Constants for chart elements that DON'T track the active theme:
// indicator overlay colors (BB, ADON, VWAP, MACD, etc.) stay consistent
// across themes so a trader's eye can read them regardless of accent.
// Accent / gain / loss are pulled from CSS vars at figure-build time.
const BLU = '#33aaff';
const YEL = '#ffee33';
const PUR = '#cc66ff';
const CYN = '#00ffcc';
const PNK = '#ff66aa';
const MUT = '#555';

// Exchange-local (US equity, America/New_York) wall-clock formatter. Bars carry
// UTC unix seconds; the intraday rangebreak below removes 16:00 -> 09:30 in ET
// hours, so the x-axis MUST be rendered in ET or the break lands on the wrong
// hours -- hiding the real afternoon session and showing empty pre-market, with
// candles at the wrong x-positions. Formatting each bar individually also
// tracks DST automatically, which a fixed UTC offset cannot.
const ET_PARTS = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York', hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
});
function toExchangeLocalIso(unixSec: number): string {
  const parts = ET_PARTS.formatToParts(new Date(unixSec * 1000));
  const g = (t: Intl.DateTimeFormatPartTypes) => parts.find(p => p.type === t)?.value ?? '00';
  // Naive ISO (no trailing Z) so Plotly reads it as wall-clock -- now ET.
  return `${g('year')}-${g('month')}-${g('day')}T${g('hour')}:${g('minute')}:${g('second')}`;
}

export interface FigureSpec {
  data:   Record<string, unknown>[];
  layout: Record<string, unknown>;
}

/** Optional options-wall input: top-N strikes by open interest with side. */
export interface OptionWall {
  strike: number;
  oi:     number;
  side:   'call' | 'put';
}

/** Pending-order bracket lines (entry / TP / SL). Each is optional. */
export interface ChartOrderLines {
  entry?: number | null;
  tp?:    number | null;
  sl?:    number | null;
}

interface BuildOpts {
  symbol:   string;
  bars:     Bar[];
  interval?: string;       // '1d' / '1h' / '5m' etc; gates rangebreak application
  overlays: OverlayId[];
  panels:   SubpanelId[];
  walls?:   OptionWall[];  // populated when `owalls` overlay is enabled
  lines?:   ChartOrderLines;
  vpSessions?: number;     // 1 = single profile across all bars; N = split into N sessions
  subFrac?:    number;     // (legacy) uniform fallback if heights map is missing
  subHeights?: Partial<Record<SubpanelId, number>>;  // per-panel heights, 0.04..0.25 each
}

/**
 * Stable ordering of order-line shapes so the relayout handler in
 * MainChart can decode `shapes[i].y0` back to the right field. Shapes
 * not currently drawn are simply absent from the array, so the index
 * is determined at build-time and exposed via the `lineShapeIndex`
 * map on the returned FigureSpec.
 */
export interface FigureSpecWithIndex extends FigureSpec {
  lineShapeIndex: { entry?: number; tp?: number; sl?: number };
}

export function buildChartFigure({ symbol, bars, interval, overlays, panels, walls, lines, vpSessions = 1, subFrac: subFracParam, subHeights }: BuildOpts): FigureSpecWithIndex {
  // Pull live accent + gain/loss from CSS vars. Lets the theme picker
  // re-color the candles + accent overlays without rewriting this function.
  const { accent: ORG, gain: GRN, loss: RED } = readThemeColors();
  // Per Python layout: main chart on top, then volume / volume profile /
  // RSI / MACD / ATR / OBV / Stochastic stacked underneath in the order
  // checked. Each subpanel takes ~12% of vertical space, main chart fills
  // the rest. Subplots in Plotly share the x-axis via `xaxisN.matches='x'`.
  const x = bars.map(b => toExchangeLocalIso(b.time));
  const open  = bars.map(b => b.open);
  const high  = bars.map(b => b.high);
  const low   = bars.map(b => b.low);
  const close = bars.map(b => b.close);
  const volume= bars.map(b => b.volume);

  // ── Subplot domain allocation ─────────────────────────────────────────
  //
  // When all subpanels are turned off the main chart should fill the
  // pane, not stay capped at the previous 70%. mainBot drops to a small
  // bottom-margin in that case. With N subpanels:
  //   bottom edge of last subpanel:  subBottomMargin
  //   stacked subpanels:             N * subFrac
  //   gap between subs and main:     subGap
  //   main chart:                    [mainBot, mainTop]
  const panelOrder: SubpanelId[] = panels;
  const numSub = panelOrder.length;
  // Resolve per-panel heights — subHeights map is the source of truth;
  // subFracParam is a legacy uniform fallback; default 0.10 if neither.
  const heights: number[] = panelOrder.map(p =>
    Math.max(0.04, Math.min(0.25, subHeights?.[p] ?? subFracParam ?? 0.10))
  );
  const totalSubH = heights.reduce((a, b) => a + b, 0);
  const subBottomMargin = 0.04;  // room for the x-axis tick labels
  const subGap = 0.02;           // gap between bottom subpanel and main chart
  const mainTop = 0.99;
  const mainBot = numSub === 0
    ? 0.05                                                  // empty → main fills 94%
    : subBottomMargin + totalSubH + subGap;
  const data: Record<string, unknown>[] = [];

  // ── Main: candlesticks ────────────────────────────────────────────────
  data.push({
    type: 'candlestick',
    name: symbol,
    x, open, high, low, close,
    increasing: { line: { color: GRN }, fillcolor: GRN },
    decreasing: { line: { color: RED }, fillcolor: RED },
    xaxis: 'x', yaxis: 'y',
    showlegend: false,
  });

  // ── Main: overlays ────────────────────────────────────────────────────
  if (overlays.includes('sma20'))  data.push(line('SMA 20',  x, SMA(close, 20),  CYN));
  if (overlays.includes('sma50'))  data.push(line('SMA 50',  x, SMA(close, 50),  YEL));
  if (overlays.includes('sma200')) data.push(line('SMA 200', x, SMA(close, 200), ORG));

  if (overlays.includes('bb')) {
    const { upper, lower, mid } = BB(close, 20, 2);
    data.push(line('BB Upper', x, upper, BLU, 'dash'));
    data.push(line('BB Mid',   x, mid,   BLU, 'dot'));
    data.push(line('BB Lower', x, lower, BLU, 'dash'));
  }

  if (overlays.includes('don')) {
    const { upper, lower, mid } = ADON(high, low, close);
    data.push(line('Don U', x, upper, PUR, 'dash'));
    data.push(line('Don M', x, mid,   PUR, 'dot'));
    data.push(line('Don L', x, lower, PUR, 'dash'));
  }

  if (overlays.includes('vwap')) {
    data.push(line('VWAP', x, VWAP(high, low, close, volume), PNK));
  }

  // Pivots are LAYOUT SHAPES rather than traces so their y-values don't
  // dilate the y-axis autorange — R3 / S3 can be far from the actual
  // candle range, which would otherwise leave huge empty space.
  // Shapes use xref:'paper' so they span the visible chart regardless
  // of x-axis zoom or rangebreak compression.
  const pivShapes: Record<string, unknown>[] = [];
  if (overlays.includes('piv')) {
    const piv = PIVOT(high, low, close);
    for (const [k, v] of Object.entries(piv)) {
      if (!Number.isFinite(v)) continue;
      const color = k === 'P' ? ORG : k.startsWith('R') ? RED : GRN;
      pivShapes.push({
        type:  'line',
        xref:  'paper', x0: 0, x1: 1,
        yref:  'y',     y0: v, y1: v,
        line:  { color, width: 1, dash: 'dot' },
        label: {
          text: `${k} $${v.toFixed(2)}`,
          font: { color, size: 9, family: "Consolas, monospace" },
          textposition: 'end',
          padding: 4,
        },
      });
    }
  }

  // Options walls — same shape approach so their strikes don't expand
  // the y-axis. Far OTM walls would otherwise force the chart to fit a
  // range way outside the candle action.
  const wallShapes: Record<string, unknown>[] = [];
  if (overlays.includes('owalls') && walls && walls.length > 0) {
    for (const w of walls) {
      const color = w.side === 'call' ? GRN : RED;
      wallShapes.push({
        type:  'line',
        xref:  'paper', x0: 0, x1: 1,
        yref:  'y',     y0: w.strike, y1: w.strike,
        line:  { color, width: 1, dash: 'longdash' },
        label: {
          text: `${w.side === 'call' ? 'CALL' : 'PUT'} ${w.strike} OI ${w.oi.toLocaleString()}`,
          font: { color, size: 9, family: "Consolas, monospace" },
          textposition: 'end',
          padding: 4,
        },
      });
    }
  }

  // ── Volume Profile overlay (right-side histogram on the main chart) ──
  //
  // Renders the volume distribution as horizontal bars hugging the right
  // edge via a secondary x-axis (`xaxis3`) with REVERSED range so x=0
  // sits on the right edge and bars extend leftward. The range max is
  // sized at ~8× the largest bar's volume so VP only occupies the right
  // ~12% of the chart width — candles behind it remain visible.
  //
  // Per-session palette is theme-agnostic so the user can see distinct
  // bands even under colorblind themes.
  let vpAxisAdded = false;
  let vpAxisMax = 0;        // for xaxis3 reversed-range upper bound
  let vpAggregate: { poc: number; vah: number; val: number } | null = null;
  if (overlays.includes('vp') && bars.length > 0) {
    // Per-session base color expressed as RGB tuples so we can render
    // the SAME hue at different opacities depending on POC / VAH-VAL /
    // outside-band membership. POC bar always uses the theme accent.
    const sessionRGB: Array<[number, number, number]> = [
      [51, 170, 255],   // blue
      [255, 153, 0],    // orange
      [204, 102, 255],  // purple
      [0, 204, 102],    // green
      [0, 255, 204],    // cyan
      [255, 102, 170],  // pink
      [255, 238, 51],   // yellow
      [255, 51, 51],    // red
      [150, 180, 255],  // light blue
      [180, 255, 180],  // light green
    ];
    // Alpha conventions:
    //   POC bin:           drawn separately in accent color (below)
    //   Inside VAH..VAL:   0.80   ← high opacity = "this is the value area"
    //   Outside the band:  0.25   ← low opacity = "context, not value"
    const ALPHA_IN = 0.80;
    // ALPHA_OUT lowered to 0.15 so the outside-band bars fade into ambient
    // context and the eye locks onto the VAH-VAL band (the actual value
    // area). Prior 0.25 was too competitive with the value zone.
    const ALPHA_OUT = 0.15;
    const nSessions = Math.max(1, Math.floor(vpSessions));
    const chunkSize = Math.max(1, Math.floor(bars.length / nSessions));
    let lastProfile: { centers: number[]; profile: number[]; poc: number; vah: number; val: number } | null = null;

    for (let s = 0; s < nSessions; s++) {
      const startIdx = s * chunkSize;
      const endIdx   = s === nSessions - 1 ? bars.length : (s + 1) * chunkSize;
      const sliceBars = bars.slice(startIdx, endIdx);
      if (sliceBars.length === 0) continue;

      const vp = VPRO(
        sliceBars.map(b => b.close),
        sliceBars.map(b => b.volume),
        sliceBars.map(b => b.high),
        sliceBars.map(b => b.low),
        40,
      );
      lastProfile = vp;

      // Per-bin color: POC drawn separately (push below), VAH-VAL band
      // at ALPHA_IN, outside band at ALPHA_OUT. We push the band bars
      // first, then the POC bar as a separate trace so it draws on
      // top with the theme accent.
      const [r, g, b] = sessionRGB[s % sessionRGB.length];
      const barColors = vp.centers.map(c => {
        if (c === vp.poc) return `rgba(${r}, ${g}, ${b}, 0)`;  // hidden, will render via POC trace
        if (c >= vp.val && c <= vp.vah) return `rgba(${r}, ${g}, ${b}, ${ALPHA_IN})`;
        return `rgba(${r}, ${g}, ${b}, ${ALPHA_OUT})`;
      });
      data.push({
        type: 'bar', orientation: 'h',
        name: nSessions > 1 ? `VP S${s + 1}` : 'Volume Profile',
        x: vp.profile, y: vp.centers,
        marker: { color: barColors },
        xaxis: 'x3', yaxis: 'y',
        showlegend: false,
        hovertemplate: '%{y:.2f} · vol %{x:.2s}<extra></extra>',
      });

      // POC bar drawn on top of the band so it pops in the accent.
      const pocIdx = vp.centers.findIndex(c => c === vp.poc);
      if (pocIdx >= 0) {
        data.push({
          type: 'bar', orientation: 'h',
          name: 'POC',
          x: [vp.profile[pocIdx]],
          y: [vp.centers[pocIdx]],
          marker: { color: ORG, line: { color: ORG, width: 1 } },
          xaxis: 'x3', yaxis: 'y',
          showlegend: false,
          hovertemplate: `POC $${vp.poc.toFixed(2)} · vol %{x:.2s}<extra></extra>`,
        });
      }

      vpAxisAdded = true;
      const sessMax = Math.max(...vp.profile);
      if (sessMax > vpAxisMax) vpAxisMax = sessMax;
    }
    if (lastProfile) {
      vpAggregate = { poc: lastProfile.poc, vah: lastProfile.vah, val: lastProfile.val };
    }
  }

  // POC / VAH / VAL chart-wide lines — also shapes for the same
  // autorange reason. POC stays prominent (2px solid), VAH/VAL are
  // dashed at 70% opacity. All use xref:paper so they span any zoom.
  const vpLineShapes: Record<string, unknown>[] = [];
  if (vpAggregate) {
    vpLineShapes.push({
      type:  'line',
      xref:  'paper', x0: 0, x1: 1,
      yref:  'y',     y0: vpAggregate.vah, y1: vpAggregate.vah,
      line:  { color: ORG, width: 1, dash: 'dash' },
      opacity: 0.7,
      label: {
        text: `VAH $${vpAggregate.vah.toFixed(2)}`,
        font: { color: ORG, size: 9, family: "Consolas, monospace" },
        textposition: 'end', padding: 4,
      },
    });
    vpLineShapes.push({
      type:  'line',
      xref:  'paper', x0: 0, x1: 1,
      yref:  'y',     y0: vpAggregate.val, y1: vpAggregate.val,
      line:  { color: ORG, width: 1, dash: 'dash' },
      opacity: 0.7,
      label: {
        text: `VAL $${vpAggregate.val.toFixed(2)}`,
        font: { color: ORG, size: 9, family: "Consolas, monospace" },
        textposition: 'end', padding: 4,
      },
    });
    // POC glow — a wider, translucent line drawn behind the crisp POC line
    // so the Point of Control visually anchors the whole profile. Pushed
    // first so it renders under the crisp line.
    vpLineShapes.push({
      type:  'line',
      xref:  'paper', x0: 0, x1: 1,
      yref:  'y',     y0: vpAggregate.poc, y1: vpAggregate.poc,
      line:  { color: ORG, width: 8 },
      opacity: 0.18,
    });
    // Crisp POC line — bumped from 2px to 3px so it stands above VAH/VAL
    // dashes without needing color divergence.
    vpLineShapes.push({
      type:  'line',
      xref:  'paper', x0: 0, x1: 1,
      yref:  'y',     y0: vpAggregate.poc, y1: vpAggregate.poc,
      line:  { color: ORG, width: 3 },
      label: {
        text: `POC $${vpAggregate.poc.toFixed(2)}`,
        font: { color: ORG, size: 10, family: "Consolas, monospace" },
        textposition: 'end', padding: 4,
      },
    });
  }

  // ── Subpanels ─────────────────────────────────────────────────────────
  const subYaxes: Record<string, Record<string, unknown>> = {};
  // Stack subpanels cumulatively — each one's top is the previous one's
  // bottom (no internal gap). Topmost subpanel starts just below mainBot.
  let cursor = mainBot - subGap;
  panelOrder.forEach((p, idx) => {
    const top = cursor;
    const bot = Math.max(0.05, top - heights[idx]);
    cursor = bot;
    const axisKey = `yaxis${idx + 2}`;
    const xaxisKey = `xaxis${idx + 2}`;
    const yref = `y${idx + 2}`;
    const xref = `x${idx + 2}`;

    subYaxes[axisKey] = {
      domain: [bot, top],
      // fixedrange prevents scroll-zoom on this subpanel y-axis. Without
      // it, scrolling over RSI / MACD / etc. zooms their y and triggers
      // a layout recompute that visibly shifts the VP histogram on the
      // main chart. With fixed y, scroll over a subpanel does nothing,
      // which is the right call for indicator panels (the y-extent is
      // bounded by indicator semantics like RSI 0..100 anyway).
      fixedrange: true,
      gridcolor: '#1a1a1a', zerolinecolor: '#222', color: MUT,
      tickfont: { size: 9, family: "Consolas, monospace" },
      title: { text: subTitle(p), font: { color: ORG, size: 10 } },
    };
    subYaxes[xaxisKey] = {
      anchor: yref, matches: 'x', showticklabels: false,
      gridcolor: '#1a1a1a',
      title: { text: '' },
    };

    switch (p) {
      case 'vol': {
        const colors = bars.map(b => b.close >= b.open ? GRN : RED);
        data.push({
          type: 'bar', name: 'Volume', x, y: volume,
          marker: { color: colors },
          xaxis: xref, yaxis: yref, showlegend: false,
        });
        break;
      }
      // 'vp' moved to main-chart overlay (right-side histogram with
      // VAH/VAL/POC lines). Subpanel branch retained for legacy state
      // payloads but emits no traces.
      case 'rsi': {
        const r = RSI(close, 14);
        data.push(line('RSI(14)', x, r, ORG, undefined, xref, yref));
        data.push({
          type: 'scatter', mode: 'lines', name: 'OB 70',
          x: [x[0], x[x.length - 1]], y: [70, 70],
          line: { color: RED, width: 1, dash: 'dot' },
          xaxis: xref, yaxis: yref, showlegend: false,
        });
        data.push({
          type: 'scatter', mode: 'lines', name: 'OS 30',
          x: [x[0], x[x.length - 1]], y: [30, 30],
          line: { color: GRN, width: 1, dash: 'dot' },
          xaxis: xref, yaxis: yref, showlegend: false,
        });
        break;
      }
      case 'macd': {
        const { macd, signal, hist } = MACD(close);
        data.push(line('MACD',   x, macd,   CYN, undefined, xref, yref));
        data.push(line('Signal', x, signal, ORG, undefined, xref, yref));
        const histColors = hist.map(h => h >= 0 ? GRN : RED);
        data.push({
          type: 'bar', name: 'MACD Hist', x, y: hist,
          marker: { color: histColors },
          xaxis: xref, yaxis: yref, showlegend: false,
        });
        break;
      }
      case 'atr': {
        const a = ATR(high, low, close, 14);
        data.push(line('ATR(14)', x, a, ORG, undefined, xref, yref));
        break;
      }
      case 'obv': {
        const o = OBV(close, volume);
        data.push(line('OBV', x, o, BLU, undefined, xref, yref));
        break;
      }
      case 'stoch': {
        const { pk, pd } = STOCH(high, low, close);
        data.push(line('%K', x, pk, CYN, undefined, xref, yref));
        data.push(line('%D', x, pd, ORG, undefined, xref, yref));
        break;
      }
    }

    // Per-panel y-axis semantics. Prevents Plotly's autorange from
    // producing misleading scales when data is sparse (e.g. RSI showing
    // 0..40 instead of the semantic 0..100 range with OB/OS bands).
    switch (p) {
      case 'rsi':
      case 'stoch':
        (subYaxes[axisKey] as Record<string, unknown>).range = [0, 100];
        (subYaxes[axisKey] as Record<string, unknown>).tickvals = [0, 30, 50, 70, 100];
        break;
      case 'vol':
        (subYaxes[axisKey] as Record<string, unknown>).rangemode = 'nonnegative';
        break;
      case 'macd':
        (subYaxes[axisKey] as Record<string, unknown>).zerolinewidth = 2;
        (subYaxes[axisKey] as Record<string, unknown>).zerolinecolor = MUT;
        break;
    }
  });

  // ── Order-line shapes (TP / SL / Entry) ───────────────────────────────
  // Draggable horizontal lines on the main yaxis. We use a paper-x span
  // (x0=0, x1=1 in 'paper' refs) so the lines always cross the full chart
  // regardless of zoom, while y stays on the price axis.
  const lineShapes: Record<string, unknown>[] = [];
  const lineShapeIndex: { entry?: number; tp?: number; sl?: number } = {};
  const lastClose = bars[bars.length - 1]?.close ?? 0;

  function makeLine(price: number, color: string, label: string) {
    const pct = lastClose > 0 ? ((price - lastClose) / lastClose) * 100 : 0;
    return {
      type:  'line',
      xref:  'paper', x0: 0, x1: 1,
      yref:  'y',     y0: price, y1: price,
      line:  { color, width: 1.6, dash: 'longdash' },
      editable: true,                       // allow drag on the y axis
      label: {
        text: `${label}  $${price.toFixed(2)}  ${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`,
        font: { color, size: 10, family: "Consolas, monospace" },
        textposition: 'top right',
        yanchor: 'bottom',
        padding: 4,
      },
    };
  }

  if (lines?.entry != null && Number.isFinite(lines.entry)) {
    lineShapeIndex.entry = lineShapes.length;
    lineShapes.push(makeLine(lines.entry, ORG, 'ENTRY'));
  }
  if (lines?.tp != null && Number.isFinite(lines.tp)) {
    lineShapeIndex.tp = lineShapes.length;
    lineShapes.push(makeLine(lines.tp, GRN, 'TP'));
  }
  if (lines?.sl != null && Number.isFinite(lines.sl)) {
    lineShapeIndex.sl = lineShapes.length;
    lineShapes.push(makeLine(lines.sl, RED, 'SL'));
  }

  // ── Layout (dark Bloomberg-style) ─────────────────────────────────────
  const layout: Record<string, unknown> = {
    paper_bgcolor: '#090909',
    plot_bgcolor:  '#090909',
    font: { color: '#e0e0e0', family: "Consolas, monospace", size: 11 },
    margin: { l: 50, r: 80, t: 8, b: 30 },
    showlegend: false,
    // Explicit empty titles on both axes (and main figure title) so
    // Plotly's `editable` mode doesn't show "Click to enter X axis
    // title" ghost text when subpanels are removed.
    title: { text: '' },
    // `autorange: true` on both axes + a `uirevision` that changes when
    // (and only when) the layout dimensions change. Plotly preserves
    // pan/zoom state across re-renders when uirevision is stable, and
    // resets to fit-content when it changes. So pan/zoom within a fixed
    // layout is sticky, but resizing any subpanel re-centers the candles.
    uirevision: `mainBot-${mainBot.toFixed(3)}-h-${heights.join(',')}-${symbol}`,
    xaxis: {
      domain: [0, 1],
      autorange: true,
      rangeslider: { visible: false },
      gridcolor: '#1a1a1a', zerolinecolor: '#222',
      tickfont: { size: 9 }, color: MUT,
      title: { text: '' },
      // Skip weekends + (for intraday intervals) after-hours so candles
      // pack tightly without gaps. Pattern `hour` with bounds [16, 9.5]
      // matches US equity market hours (NYSE 9:30am-4pm ET). For daily+
      // intervals we still skip weekends but not hours.
      rangebreaks: (() => {
        const breaks: Record<string, unknown>[] = [{ bounds: ['sat', 'mon'] }];
        if (interval && /[smh]$/i.test(interval)) {
          breaks.push({ bounds: [16, 9.5], pattern: 'hour' });
        }
        return breaks;
      })(),
    },
    yaxis: {
      domain: [mainBot, mainTop],
      autorange: true,
      gridcolor: '#1a1a1a', zerolinecolor: '#222',
      tickfont: { size: 10 }, color: MUT,
      side: 'right',
      title: { text: '' },
    },
    ...subYaxes,
    // VP overlay axis — secondary x-axis overlaid on the main one.
    // Reversed range: max at left (x=0 at right edge) so bars draw
    // from the right edge leftward. Range max = vpAxisMax × 8 so the
    // Multiplier bumped from 8 → 10 so the longest bar occupies ~10% of
    // chart width instead of ~12%. Candles behind the profile get more
    // breathing room. Hidden ticks + grid (purely decorative axis).
    ...(vpAxisAdded ? {
      xaxis3: {
        overlaying:     'x',
        side:           'top',
        domain:         [0, 1],                // span the full plot width
        showgrid:       false,
        showticklabels: false,
        zeroline:       false,
        // Explicit reversed range with autorange OFF and fixedrange ON.
        // Without these, Plotly re-fits the axis to the data extent and
        // un-reverses it, which floats the VP bars into the middle of the
        // chart instead of anchoring them to the right edge.
        autorange:      false,
        fixedrange:     true,
        range:          [vpAxisMax * 10, 0],   // reversed: 0 renders at RIGHT
        anchor:         'y',
        constrain:      'range',
      },
    } : {}),
    // Order shapes (TP/SL/Entry — editable for drag) come FIRST so the
    // lineShapeIndex returned by this function correctly maps to the
    // first N shape indices for the relayout drag handler in MainChart.
    // Pivot / wall / POC-VAH-VAL shapes append after — they're not
    // editable, so they never get matched in the drag-handler regex.
    shapes: [...lineShapes, ...pivShapes, ...wallShapes, ...vpLineShapes],
    dragmode: 'pan',
    hovermode: 'x unified',
    // When the VP overlay is on, render its bars with a slight gap so
    // adjacent bins don't bleed into each other. barmode:'overlay' so
    // multi-session profiles stack visually instead of being grouped.
    barmode: vpAxisAdded ? 'overlay' : 'group',
    bargap: 0.05,
  };

  return { data, layout, lineShapeIndex };
}

// ── Helpers ──────────────────────────────────────────────────────────────

function line(
  name: string, x: string[], y: number[], color: string, dash?: string,
  xref = 'x', yref = 'y',
): Record<string, unknown> {
  return {
    type: 'scatter', mode: 'lines',
    name, x, y,
    line: { color, width: 1.4, dash },
    xaxis: xref, yaxis: yref,
    connectgaps: false,
    showlegend: false,
  };
}

function subTitle(p: SubpanelId): string {
  switch (p) {
    case 'vol':   return 'Volume';
    case 'rsi':   return 'RSI(14)';
    case 'macd':  return 'MACD';
    case 'atr':   return 'ATR(14)';
    case 'obv':   return 'OBV';
    case 'stoch': return 'Stoch';
  }
}
